<script setup lang="ts">
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import IconGlobe from '~/assets/icons/icon-globe.svg?component'

const emit = defineEmits<{
  navbarClose: []
}>()

const { locales, locale, t } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const route = useRoute()
const shouldShowLanguagePicker = computed(
  () => !String(route.name).includes('magazine')
)

const { data: eventListPageData } = useEventListPageStore()

const getLocaleUrl = (localeCode: string): string => {
  if (eventListPageData.value) {
    const { buildLocaleUrl } = useEventListPageLocaleSwitcher(
      eventListPageData.value
    )
    const customUrl = buildLocaleUrl(localeCode)
    if (customUrl) {
      return customUrl
    }
  }

  return switchLocalePath(localeCode as any)
}

const getShortLocaleLabel = (localeCode: string): string => {
  const translationKey = `general.languages.shortCodes.${localeCode}` as any
  return t(translationKey) || localeCode.toUpperCase()
}

const currentLocaleShort = computed(() => getShortLocaleLabel(locale.value))

const props = withDefaults(
  defineProps<{
    iconSize?: number
    textClass?: string
    openDirection?: 'up' | 'down'
  }>(),
  {
    iconSize: 16,
    textClass: 'text-gray-800 text-caption font-medium',
    openDirection: 'up',
  }
)
</script>

<template>
  <div v-if="shouldShowLanguagePicker" class="flex items-center">
    <Menu as="div" class="relative inline-block">
      <MenuButton :class="['flex items-center gap-2', props.textClass]">
        <IconGlobe :size="props.iconSize" class="text-stone-700 stroke-stone-700 stroke-width-1.67" />
        <span>{{ currentLocaleShort }}</span>
      </MenuButton>
      <MenuItems
        :class="[
          'absolute left-0 flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10 min-w-[80px]',
          props.openDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2',
        ]"
      >
        <MenuItem
          v-for="loc in locales"
          :key="loc.code"
          v-slot="{ active, close }"
        >
          <button
            type="button"
            :class="[
              'px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 w-full text-left',
              active ? 'bg-gray-50' : '',
              locale === loc.code ? 'font-semibold' : '',
            ]"
            @click="
              () => {
                close()
                emit('navbarClose')
                navigateTo(getLocaleUrl(loc.code))
              }
            "
          >
            {{ getShortLocaleLabel(loc.code) }}
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  </div>
</template>
